import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { affordableHousingComApi } from "api/affordableHousingCom";
import { rentalBeastApi } from "api/rentalBeast";
import { uloopApi, UloopConnectPayload } from "api/uloop";
import { rentalSourceApi } from "api/rentalSource";
import { zillowApi } from "api/zillow";
import { zumperApi } from "api/zumper";
import { dwellsyApi } from "api/dwellsy";
import { apartmentsComApi } from "api/apartmentsCom";
import {
  FeedConnectionStatus,
  FeedConnectPayload,
  SyndicationFeed
} from "api/syndicationFeed";
import { useAuthContext } from "providers/Auth";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { UloopPricing } from "types/landlord/partner";
import { UloopPricingModel } from "views/settings-integration/integrations/shared/UloopPricingModel";
import { getErrorMessage } from "utils/error";
import { getFeedErrorMessage } from "views/settings-integration/integrations/shared/feedErrorMessage";
import { ListingSelectionTree } from "views/settings-integration/integrations/shared/ListingSelectionTree";
import {
  ListingSelection,
  connectPayload,
  isUnitSelected,
  selectAll,
  selectNone,
  selectedCount,
  setUnitsSelected
} from "views/settings-integration/integrations/shared/listingSelection";
import {
  PickerUnit,
  fetchAllPickerUnits,
  isListableUnit
} from "views/settings-integration/integrations/shared/pickerUnits";
import { invalidateFeedQueries } from "views/settings-integration/integrations/shared/useSyndicationFeed";

interface ChooseListingsStepProps {
  feed: SyndicationFeed;
  feedLabel: string;
  closeModal: () => void;
  onBack: () => void;
}

const CONNECT_BY_FEED: Record<
  SyndicationFeed,
  (payload: FeedConnectPayload) => Promise<FeedConnectionStatus>
> = {
  zillow: zillowApi.connect,
  zumper: zumperApi.connect,
  dwellsy: dwellsyApi.connect,
  apartments_com: apartmentsComApi.connect,
  affordable_housing_com: (_payload: FeedConnectPayload) =>
    affordableHousingComApi.connect() as Promise<FeedConnectionStatus>,
  rental_beast: (payload: FeedConnectPayload) =>
    rentalBeastApi.connect(payload) as Promise<FeedConnectionStatus>,
  uloop: async (payload: FeedConnectPayload): Promise<FeedConnectionStatus> => {
    const result = await uloopApi.connect(payload as UloopConnectPayload);
    return {
      connected: result.connected,
      auto_add_listings: Boolean(result.auto_add_listings)
    };
  },
  rental_source: (payload: FeedConnectPayload) =>
    rentalSourceApi.connect(payload) as Promise<FeedConnectionStatus>
};

const selectionScopeLabel = (
  listings: number,
  properties: number,
  propertyLabel: string,
  propertiesLabel: string
) => {
  if (listings === 0) return "No listings selected";
  return (
    `${listings} ${listings === 1 ? "listing" : "listings"} across ` +
    `${properties} ${properties === 1 ? propertyLabel : propertiesLabel} selected`
  );
};

const countSelectedProperties = (
  units: PickerUnit[],
  selection: ListingSelection
) =>
  new Set(
    units
      .filter((unit) => isUnitSelected(selection, unit.id))
      .map((unit) => unit.property?.id)
      .filter((id): id is number => Boolean(id))
  ).size;

export const ChooseListingsStep = ({
  feed,
  feedLabel,
  closeModal,
  onBack
}: ChooseListingsStepProps) => {
  const queryClient = useQueryClient();
  const { refreshPartnerData } = useAuthContext();
  const { property: propertyLabel, properties: propertiesLabel } =
    useUnitsPropertiesDictionary();
  // Starts as "everything", so the default costs nothing to express and stays correct
  // however large the portfolio is.
  const [selection, setSelection] = useState<ListingSelection>(selectAll);
  const [uloopPricing, setUloopPricing] = useState<UloopPricing>("lead");

  const { data: unitsData, isLoading: isLoadingUnits } = useQuery({
    queryKey: ["CHOOSE_LISTINGS_ALL_UNITS"],
    queryFn: fetchAllPickerUnits,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const units = useMemo(() => unitsData ?? [], [unitsData]);
  const eligibleUnits = useMemo(() => units.filter(isListableUnit), [units]);

  const isSelected = useCallback(
    (unit: PickerUnit) => isUnitSelected(selection, unit.id),
    [selection]
  );

  const toggleUnit = useCallback(
    (unit: PickerUnit) =>
      setSelection((prev) =>
        setUnitsSelected(prev, [unit.id], !isUnitSelected(prev, unit.id))
      ),
    []
  );

  const toggleMany = useCallback(
    (unitsToSet: PickerUnit[], select: boolean) =>
      setSelection((prev) =>
        setUnitsSelected(
          prev,
          unitsToSet.map((unit) => unit.id),
          select
        )
      ),
    []
  );

  const connectMutation = useMutation({
    mutationKey: [feed.toUpperCase(), "CREATE"],
    mutationFn: (payload: FeedConnectPayload) => CONNECT_BY_FEED[feed](payload),
    onSuccess: () => {
      invalidateFeedQueries(queryClient, feed);
      refreshPartnerData();
      toast.success(`${feedLabel} integration connected successfully`);
      closeModal();
    },
    onError: (error) => toast.error(getFeedErrorMessage(error))
  });

  const totalCount = eligibleUnits.length;
  const selectedTotal = selectedCount(selection, totalCount);
  const selectedPropertyCount = useMemo(
    () => countSelectedProperties(eligibleUnits, selection),
    [eligibleUnits, selection]
  );

  const selectionSummary = () => {
    if (totalCount === 0) return "";
    if (selectedTotal === 0)
      return `No listings selected — ${feedLabel} will connect with an empty feed`;
    if (selectedTotal === totalCount)
      return `All ${totalCount} ${totalCount === 1 ? "listing" : "listings"} selected`;
    return `${selectedTotal} of ${totalCount} listings selected`;
  };

  const buildConnectPayload = (): UloopConnectPayload => {
    const base = connectPayload(selection);
    if (feed === "uloop") return { ...base, pricing_model: uloopPricing };
    return base;
  };

  return (
    <>
      <Show if={feed === "uloop"}>
        <UloopPricingModel
          className="mb-6"
          value={uloopPricing}
          onChange={setUloopPricing}
        />
      </Show>

      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-content-400">
          {selectionScopeLabel(
            selectedTotal,
            selectedPropertyCount,
            propertyLabel,
            propertiesLabel
          )}
        </p>
        <div className="flex items-center gap-3">
          <Button
            variant={ButtonVariant.TEXT}
            size={ButtonSize.SM}
            onClick={() => setSelection(selectAll())}
            disabled={totalCount === 0}
          >
            Select all
          </Button>
          <span className="h-3 w-px bg-alpha-100" />
          <Button
            variant={ButtonVariant.TEXT}
            size={ButtonSize.SM}
            onClick={() => setSelection(selectNone())}
            disabled={selectedTotal === 0}
          >
            Select none
          </Button>
        </div>
      </div>

      <ListingSelectionTree
        units={units}
        isEligible={isListableUnit}
        isSelected={isSelected}
        onToggleUnit={toggleUnit}
        onToggleMany={toggleMany}
        isLoading={isLoadingUnits}
      />

      <div className="mt-4 flex items-center justify-between gap-4">
        <p
          className={
            selectedTotal === 0 && totalCount > 0
              ? "text-sm text-primary-600"
              : "text-sm text-content-400"
          }
        >
          {selectionSummary()}
        </p>
        <div className="flex shrink-0 gap-2">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.BASE}
            onClick={onBack}
          >
            Back
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            onClick={() => connectMutation.mutate(buildConnectPayload())}
            loading={connectMutation.isPending}
            disabled={connectMutation.isPending || isLoadingUnits}
          >
            Connect {feedLabel}
          </Button>
        </div>
      </div>
    </>
  );
};
